import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Security as SecurityIcon } from '@mui/icons-material';
import usersService, { UserRecord, UserRole } from '../services/usersService';
import { useTranslation } from '../i18n/useTranslation';
import PrivilegesMatrix from './PrivilegesMatrix';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { setUserPrivileges } from '../store/authSlice';

// Support legacy privilege keys by mapping to resource:action if needed
function normalizePrivilege(p: string): string {
  if (p.includes(':')) return p;
  // basic legacy to matrix mapping
  const map: Record<string, string[]> = {
    manage_users: ['users:create','users:update','users:delete','users:read'],
    manage_rules: ['rules:create','rules:update','rules:delete','rules:read'],
    view_reports: ['reports:read'],
    submit_applications: ['applications:create'],
    view_status: ['applications:read']
  };
  const expanded = map[p];
  return expanded ? expanded[0] : p; // used only for visualization; view uses full array below
}

const DEFAULT_LIMIT = 10;

const ALL_PRIVILEGES = [
  'manage_users',
  'manage_rules',
  'view_reports',
  'submit_applications',
  'view_status'
];

const roleToSuggestedPrivileges: Record<UserRole, string[]> = {
  bank: ['manage_users', 'manage_rules', 'view_reports'],
  wave: ['view_reports'],
  merchant: ['submit_applications', 'view_status']
};

const UsersManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const currentUserEmail = useSelector((s: RootState) => s.auth.user?.email);

  const [records, setRecords] = useState<UserRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [openView, setOpenView] = useState<null | UserRecord>(null);
  const [editRecord, setEditRecord] = useState<UserRecord | null>(null);
  const [form, setForm] = useState<{ email: string; name: string; role: UserRole; status: string; privileges: string[] }>(
    { email: '', name: '', role: 'merchant', status: 'active', privileges: roleToSuggestedPrivileges['merchant'].map(p => p.includes(':') ? p : p) }
  );

  const load = async (p = page, q = search) => {
    setLoading(true);
    try {
      const res = await usersService.list({ page: p, limit: DEFAULT_LIMIT, q });
      setRecords(res.data);
      setTotalPages(res.pagination.totalPages || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1, ''); }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await load(1, search);
    setPage(1);
  };

  const openCreate = () => {
    setEditRecord(null);
    setForm({ email: '', name: '', role: 'merchant', status: 'active', privileges: roleToSuggestedPrivileges['merchant'] });
    setOpenDialog(true);
  };

  const openEdit = (rec: UserRecord) => {
    setEditRecord(rec);
    setForm({ email: rec.email, name: rec.name, role: rec.role, status: rec.status, privileges: rec.privileges || [] });
    setOpenDialog(true);
  };

  const submit = async () => {
    if (editRecord) {
      const updated = await usersService.update(editRecord.id, { name: form.name, role: form.role, status: form.status, privileges: form.privileges });
      if (currentUserEmail && (editRecord.email === currentUserEmail || updated.email === currentUserEmail)) {
        dispatch(setUserPrivileges(updated.privileges || []));
      }
    } else {
      await usersService.create({ email: form.email, name: form.name, role: form.role, status: form.status, privileges: form.privileges });
    }
    setOpenDialog(false);
    await load();
  };

  const remove = async (rec: UserRecord) => {
    if (window.confirm(t('message.confirmDelete'))) {
      await usersService.remove(rec.id);
      await load();
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setForm(prev => ({ ...prev, role, privileges: roleToSuggestedPrivileges[role] }));
  };

  // privilege chip helpers
  const getActionColor = (action: string) => {
    if (action === 'read') return 'default';
    if (action === 'create') return 'success';
    if (action === 'update') return 'info';
    if (action === 'delete') return 'warning';
    return 'default';
  };

  const filteredRecords = records.filter(r => (roleFilter === 'all' ? true : r.role === roleFilter));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('users.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('users.addUser')}
        </Button>
      </Stack>

      <Box component="form" onSubmit={handleSearch} sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <TextField size="small" select label={t('settings.role')} value={roleFilter} onChange={e => setRoleFilter(e.target.value as any)} sx={{ width: 180 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="bank">BANK</MenuItem>
          <MenuItem value="wave">WAVE</MenuItem>
          <MenuItem value="merchant">MERCHANT</MenuItem>
        </TextField>
        <Button type="submit" variant="outlined">{t('common.search')}</Button>
      </Box>

      <Box sx={{ borderRadius: 2, background: 'white', p: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 2fr 1fr 0.8fr', gap: 1, fontWeight: 600, mb: 1 }}>
          <Box>{t('reports.email')}</Box>
          <Box>{t('reports.name')}</Box>
          <Box>{t('settings.role')}</Box>
          <Box>{t('users.privileges')}</Box>
          <Box>{t('common.status')}</Box>
          <Box>{t('common.actions')}</Box>
        </Box>
        {filteredRecords.map(r => (
          <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 2fr 1fr 0.8fr', gap: 1, alignItems: 'center', py: 1, borderTop: '1px solid #eee' }}>
            <Box>{r.email}</Box>
            <Box>{r.name}</Box>
            <Box><Chip size="small" label={r.role.toUpperCase()} color={r.role === 'bank' ? 'primary' : r.role === 'wave' ? 'info' : 'default'} /></Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(r.privileges || []).slice(0, 6).map(p => {
                const [res, act] = p.split(':');
                return (
                  <Tooltip key={p} title={`${t(`priv.resource.${res}`)} • ${t(`priv.action.${act}`)}`}>
                    <Chip size="small" icon={<SecurityIcon fontSize="small" />} label={p} color={getActionColor(act) as any} variant={act === 'read' ? 'outlined' : 'filled'} />
                  </Tooltip>
                );
              })}
              {(r.privileges?.length || 0) > 6 && (
                <Button size="small" onClick={() => setOpenView(r)} sx={{ textTransform: 'none' }}>
                  +{(r.privileges!.length - 6)} more
                </Button>
              )}
            </Box>
            <Box><Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /></Box>
            <Box>
              <Tooltip title={t('common.view')}><IconButton onClick={() => setOpenView(r)}><SecurityIcon /></IconButton></Tooltip>
              <Tooltip title={t('common.edit')}><IconButton onClick={() => openEdit(r)}><EditIcon /></IconButton></Tooltip>
              <Tooltip title={t('common.delete')}><IconButton color="error" onClick={() => remove(r)}><DeleteIcon /></IconButton></Tooltip>
            </Box>
          </Box>
        ))}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Pagination page={page} onChange={async (_, p) => { setPage(p); await load(p, search); }} count={totalPages} />
        </Stack>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRecord ? t('users.editUser') : t('users.addUser')}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!editRecord && (
            <TextField label={t('reports.email')} value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} required />
          )}
          <TextField label={t('reports.name')} value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
          <TextField select label={t('settings.role')} value={form.role} onChange={e => handleRoleChange(e.target.value as UserRole)}>
            <MenuItem value="bank">BANK</MenuItem>
            <MenuItem value="wave">WAVE</MenuItem>
            <MenuItem value="merchant">MERCHANT</MenuItem>
          </TextField>
          <TextField select label={t('common.status')} value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}>
            <MenuItem value="active">{t('accountLinking.active')}</MenuItem>
            <MenuItem value="inactive">{t('accountLinking.inactive')}</MenuItem>
          </TextField>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('users.privileges')}</Typography>
            <PrivilegesMatrix
              value={form.privileges}
              onChange={(next) => setForm(prev => ({ ...prev, privileges: next }))}
              resources={[ 'applications','reports','rules','users','merchants','transactions','settings','teams' ]}
              actions={[ 'create','delete','read','update' ]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* View privileges dialog */}
      <Dialog open={!!openView} onClose={() => setOpenView(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('users.title')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {openView && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2"><b>{t('reports.email')}:</b> {openView.email}</Typography>
              <Typography variant="body2"><b>{t('reports.name')}:</b> {openView.name}</Typography>
              <Typography variant="body2"><b>{t('settings.role')}:</b> {openView.role.toUpperCase()}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('users.privileges')}</Typography>
              <PrivilegesMatrix
                value={(openView.privileges || []).map(p => normalizePrivilege(p))}
                resources={[ 'applications','reports','rules','users','merchants','transactions','settings','teams' ]}
                actions={[ 'create','delete','read','update' ]}
                readOnly
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersManagement;


